import React from 'react';
import noop from 'lodash/noop';
import cn from 'classnames';
import Select, { components as ReactSelectComponents } from 'react-select';
import { PAGINATOR_ITEMS_PER_PAGE_COUNT, PAGINATOR_ITEMS_PER_PAGE_OPTIONS } from './constants';
import { customPaginatorStyles, selectTheme } from '@creditflowresearch/uilib/Select/selectConfig';
import Pagination from './Pagination';

// Assets
import arrowPreviousSvg from './assets/arrow-previous.inline.svg?raw';
import arrowNextSvg from './assets/arrow-next.inline.svg?raw';

// Styles
import selectStyles from '@creditflowresearch/uilib/Select/select.module.scss';
import styles from './paginator.module.scss';

export interface IProps {
  /**
   * Optional CSS class name for custom styling
   */
  className?: string,
  /**
   * Text or React node to be displayed as a label
   */
  labelOfText?: string | React.ReactNode,
  /**
   * Total number of items to paginate
   */
  totalCount: number,
  /**
   * Current page number
   */
  page: number,
  /**
   * Number of items to display per page
   */
  perPage?: number,
  /**
   * Boolean indicating if the items per page selector should be visible
   */
  isItemsPerPageVisible?: boolean,
  /**
   * Callback function triggered when the items per page value changes
   */
  onItemsPerPageChange?: (params: { value: number }) => void,
  /**
   * Callback function triggered when the page changes
   */
  onPageChange?: (page: { selected: number }) => void,
  /**
   * Element or selector to scroll to when the page changes
   */
  scrollOnChange?: React.RefObject<HTMLElement> | string,
  /**
   * Options for scrolling behavior
   */
  scrollOptions?: { offset?: number, scrollContainerId?: string },
  /**
   * Unique identifier for testing purposes
   */
  dataTest: string,
  /**
   * Custom options for items per page selection
   */
  customOptions?: { label: string, value: number }[],
  /**
   * Custom renderer for the value display
   */
  valueRenderer?: (props?: any) => React.JSX.Element,
  /**
   * CSS class name for the items per page selector
   */
  perPageClassName?: string,
  /**
   * Minimum value for items per page
   */
  minPerPageValue?: number,
  /**
   * Boolean indicating if the paginator is in a loading state
   */
  isLoading?: boolean,
  /**
   * Custom components for rendering within the paginator
   */
  components?: { [key: string]: any },
  /**
   * Boolean indicating if the paginator is disabled
   */
  isDisabled?: boolean,
}

const Paginator: React.FC<IProps> = (props) => {
  const {
    valueRenderer: ValueRenderer,
    customOptions,
    className,
    totalCount,
    labelOfText = '',
    page,
    perPage = PAGINATOR_ITEMS_PER_PAGE_COUNT,
    onItemsPerPageChange = noop,
    onPageChange = noop,
    isItemsPerPageVisible = true,
    dataTest,
    perPageClassName,
    minPerPageValue,
    isDisabled,
  } = props;
  const isPaginationAvailable = !!totalCount;
  const currentMinPerPageValue = minPerPageValue || PAGINATOR_ITEMS_PER_PAGE_COUNT;

  const isItemsPerPageAvailable = isItemsPerPageVisible
    ? (!!totalCount && totalCount > currentMinPerPageValue)
    : isItemsPerPageVisible;

  const offsetFrom = ((page - 1) * perPage) + 1;
  let offsetTo = page * perPage;
  offsetTo = offsetTo > totalCount ? totalCount : offsetTo;

  const getDataTest = (label) => {
    const dataLabel = label && typeof label === 'string' && label.toLowerCase().replace(/\W+/g, '_');
    return dataLabel ? `${ dataTest }_${ dataLabel }` : dataTest;
  };

  const getOptionRenderer = (option) => {
    const { isSelected } = option;
    return (
      <div data-test={ getDataTest('option') } className={ cn({ 'is-selected': isSelected }) }>
        <ReactSelectComponents.Option { ...option } />
      </div>
    );
  };

  const getValueContainerRenderer = (valueContainerProps) => {
    const { children, ...otherProps } = valueContainerProps;
    const { components = {}, isLoading } = props;
    let cut = 0;
    if (isLoading) cut += 21;
    if (!components.DropdownIndicator) cut += 20;
    const width = `calc(100% - ${ cut }px)`;
    const style = {
      ...valueContainerProps.getStyles('valueContainer', valueContainerProps),
      padding: 0,
      width,
      lineHeight: '26px',
    };

    return (
      <div data-test={ getDataTest('value_container') } style={ style }>
        <ReactSelectComponents.ValueContainer { ...otherProps }>{ children }</ReactSelectComponents.ValueContainer>
      </div>
    );
  };

  const getValueRenderer = (props) => {
    const { children, ...otherProps } = props;

    if (ValueRenderer) {
      return (
        <div
          data-test={ getDataTest('selected') }
          style={ props.getStyles('singleValue', props) }
        >
          <ReactSelectComponents.SingleValue { ...otherProps }>
            <ValueRenderer { ...props.data } />
          </ReactSelectComponents.SingleValue>
        </div>
      );
    }
    return (
      <div
        data-test={ getDataTest('selected') }
        style={ props.getStyles('singleValue', props) }
      >
        <ReactSelectComponents.SingleValue { ...otherProps }>
          { children }
        </ReactSelectComponents.SingleValue>
      </div>
    );
  };

  if (isPaginationAvailable) {
    return (
      <div
        className={ cn(styles.paginatorWrapper, className) }
        data-test={ `${ dataTest }Pagination` }
      >
        <div
          className={ cn(styles.paginatorInfo) }
          data-test="pageInfo"
        >
          { offsetFrom } to { offsetTo } of { totalCount } { labelOfText }
        </div>
        <div className={ styles.paginatorControls }>
          { isItemsPerPageAvailable && (
            <div
              className={ cn(selectStyles.selectEncapsulation, styles.itemsPerPage, perPageClassName) }
              data-test={ `${ dataTest }PerPageSelect` }
            >
              <Select
                name="itemsPerPage"
                className={ cn(['Select', 'drop-up']) }
                theme={ selectTheme }
                styles={ customPaginatorStyles }
                menuPlacement="top"
                components={ {
                  Option: getOptionRenderer,
                  SingleValue: getValueRenderer,
                  ValueContainer: getValueContainerRenderer,
                  DropdownIndicator: (dropdownProps) => (
                    <ReactSelectComponents.DropdownIndicator
                      { ...dropdownProps }
                      className={ cn(dropdownProps.className, selectStyles.dropdownIndicator) }
                    />
                  ),
                } }
                isClearable={ false }
                isSearchable={ false }
                value={
                  customOptions ?
                    customOptions.filter(({ value }) => value === perPage) :
                    PAGINATOR_ITEMS_PER_PAGE_OPTIONS.filter(({ value }) => value === perPage)
                }
                options={ customOptions || PAGINATOR_ITEMS_PER_PAGE_OPTIONS }
                onChange={ onItemsPerPageChange }
                isDisabled={ isDisabled }
              />
            </div>
          ) }
          <div className={ styles.paginationWrp }>
            <Pagination
              totalItemsCount={ totalCount }
              itemsCountPerPage={ perPage }
              page={ page }
              onPageChange={ onPageChange }
              className={ styles.pagination }
              inputWrpClassName={ styles.paginationInputWrp }
              inputClassName={ styles.paginationInput }
              disabledClassName={ styles.arrowIsDisabled }
              nextClassName={ styles.arrowNext }
              nextLabel={ (
                <span dangerouslySetInnerHTML={ { __html: arrowNextSvg } } />
              ) }
              previousClassName={ styles.arrowPrevious }
              previousLabel={ (
                <span dangerouslySetInnerHTML={ { __html: arrowPreviousSvg } } />
              ) }
              scrollOnChange={ props.scrollOnChange }
              scrollOptions={ props.scrollOptions }
              dataTest={ dataTest }
              isDisabled={ isDisabled }
            />
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default Paginator;
